import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'ra_admin_token';

interface StageCounts {
  visits: number;
  activated: number;
  rebuilt: number;
  paid: number;
  revenue_usd: number;
}

interface SourceRow extends StageCounts {
  source: string;
  medium: string;
  campaign: string;
  conv_visit_to_paid: number;
}

interface ThemeRow extends StageCounts {
  variant: string;
  conv_visit_to_activated: number;
  conv_visit_to_paid: number;
}

interface FunnelReport {
  generated_at: string;
  range_days: number | null;
  totals: StageCounts & {
    conv_visit_to_activated: number;
    conv_activated_to_paid: number;
    conv_visit_to_paid: number;
  };
  by_source: SourceRow[];
  by_theme: ThemeRow[];
  wizard?: { event: string; label: string; events: number; users: number; conv_from_view: number }[];
}

const money = (n: number) => `$${(n || 0).toFixed(2)}`;
const pct = (n: number) => `${(n || 0).toFixed(1)}%`;

// A single step in the headline funnel bar.
function FunnelStep({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center', minWidth: 120 }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {value.toLocaleString()}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {sub && (
        <Typography variant="caption" color="primary.main">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

export default function AdminMarketing() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  // 'all' = no date filter (the API treats a missing `days` as all-time).
  const [days, setDays] = useState<number | 'all'>('all');
  const [report, setReport] = useState<FunnelReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/marketing`, {
        params: days !== 'all' ? { days } : {},
        headers: { 'X-Admin-Token': token },
      });
      setReport(res.data);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 401 || status === 503) {
        setError('Invalid or unconfigured admin token.');
        setToken('');
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setError(err.response?.data?.detail || 'Failed to load report.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    load();
  }, [load]);

  const saveToken = () => {
    const t = tokenInput.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Marketing dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter the admin token to view acquisition + conversion data.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <TextField
            type="password"
            label="Admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveToken()}
            fullWidth
            size="small"
          />
          <Button variant="contained" onClick={saveToken}>
            Enter
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Container>
    );
  }

  const t = report?.totals;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Marketing funnel</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, v) => v !== null && setDays(v)}
        >
          <ToggleButton value={7}>7d</ToggleButton>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={90}>90d</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {t && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <FunnelStep label="Visits" value={t.visits} />
            <FunnelStep
              label="Optimized"
              value={t.activated}
              sub={`${pct(t.conv_visit_to_activated)} of visits`}
            />
            <FunnelStep label="Built PDF" value={t.rebuilt} />
            <FunnelStep
              label="Paid"
              value={t.paid}
              sub={`${pct(t.conv_activated_to_paid)} of optimized`}
            />
            <FunnelStep label="Revenue" value={t.revenue_usd} sub={money(t.revenue_usd)} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Overall visit → paid conversion: <strong>{pct(t.conv_visit_to_paid)}</strong>
            {report?.range_days ? ` · last ${report.range_days} days` : ' · all time'}
          </Typography>

          {report?.wizard && report.wizard.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Optimize wizard funnel
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Distinct people at each step of the 2-step optimize wizard (résumé → job → submit).
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {report.wizard.map((w, i) => (
                  <FunnelStep
                    key={w.event}
                    label={w.label}
                    value={w.users}
                    sub={i === 0 ? `${w.events} views` : `${pct(w.conv_from_view)} of viewers`}
                  />
                ))}
              </Stack>
            </>
          )}

          <Typography variant="h6" gutterBottom>
            Theme A/B test
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sorted by activation (visit → first optimization), the primary metric.
            Rows with fewer than ~30 visits aren&apos;t yet statistically meaningful.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Theme</TableCell>
                  <TableCell align="right">Visits</TableCell>
                  <TableCell align="right">Optimized</TableCell>
                  <TableCell align="right">Visit→Opt</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Visit→Paid</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report!.by_theme || []).map((r, i) => (
                  <TableRow key={r.variant} hover selected={i === 0 && r.visits >= 5}>
                    <TableCell>
                      <Chip
                        label={r.variant}
                        size="small"
                        color={i === 0 && r.visits >= 5 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{r.visits}</TableCell>
                    <TableCell align="right">{r.activated}</TableCell>
                    <TableCell align="right">
                      <strong>{pct(r.conv_visit_to_activated)}</strong>
                    </TableCell>
                    <TableCell align="right">{r.paid}</TableCell>
                    <TableCell align="right">{pct(r.conv_visit_to_paid)}</TableCell>
                    <TableCell align="right">{money(r.revenue_usd)}</TableCell>
                  </TableRow>
                ))}
                {(report!.by_theme || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom>
            By source
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="right">Visits</TableCell>
                  <TableCell align="right">Optimized</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Visit→Paid</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report!.by_source.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Chip label={r.source} size="small" />
                    </TableCell>
                    <TableCell>{r.medium}</TableCell>
                    <TableCell>{r.campaign}</TableCell>
                    <TableCell align="right">{r.visits}</TableCell>
                    <TableCell align="right">{r.activated}</TableCell>
                    <TableCell align="right">{r.paid}</TableCell>
                    <TableCell align="right">{pct(r.conv_visit_to_paid)}</TableCell>
                    <TableCell align="right">{money(r.revenue_usd)}</TableCell>
                  </TableRow>
                ))}
                {report!.by_source.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No data yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
}
